import React, { useState } from 'react';
import { auth, db } from '../firebaseConfig';
import { createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, updateProfile } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { showToast } from '../components/Toast';

function ModalRegister({ onClose }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const createUserDocumentFunction = async (user, additionalData = {}) => {
    if (!user) return;

    try {
      const userRef = doc(db, 'users', user.uid);
      
      const userData = {
        email: user.email,
        displayName: user.displayName || displayName || 'Usuario',
        photoURL: user.photoURL || null,
        role: 'client',
        createdAt: serverTimestamp(),
        ...additionalData
      };

      await setDoc(userRef, userData, { merge: true });
      return true;
    } catch (error) {
      console.error('❌ Error al crear perfil de usuario en Firestore:', error);
      
      if (error.code === 'permission-denied') {
        showToast('Permission denied: Check Firestore security rules', 'error', true);
      } else if (error.code === 'not-found') {
        showToast('The "users" collection does not exist in Firestore', 'error', true);
      } else {
        showToast('Error creating user profile: ' + error.message, 'error', true);
      }
      throw error;
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (!displayName.trim()) {
      setError('El nombre es requerido');
      return;
    }

    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      await updateProfile(userCredential.user, {
        displayName: displayName
      });

      await createUserDocumentFunction(userCredential.user, { displayName });

      showToast('¡Registro exitoso! Bienvenido a Starboy Records', 'success');
      
      setLoading(false);
      
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      setLoading(false);
      if (error.code === 'auth/email-already-in-use') {
        setError('Este email ya está registrado');
      } else if (error.code === 'auth/invalid-email') {
        setError('Email inválido');
      } else if (error.code === 'auth/weak-password') {
        setError('La contraseña es muy débil');
      } else {
        setError('Error al registrarse: ' + (error.message || 'Error desconocido'));
      }
    }
  };

  const handleGoogleRegister = async () => {
    setError('');
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      provider.setCustomParameters({
        'prompt': 'consent'
      });

      const result = await signInWithPopup(auth, provider);
      
      // Crear documento del usuario
      await createUserDocumentFunction(result.user, { displayName: result.user.displayName });
      
      showToast('¡Registro con Google exitoso!', 'success');
      
      setLoading(false);
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      setLoading(false);
      
      if (error.code === 'auth/operation-not-allowed') {
        setError('Google Sign-in no está habilitado en Firebase');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Error de conexión. Intenta de nuevo');
      } else if (error.code === 'auth/popup-blocked') {
        setError('El popup fue bloqueado. Permite popups en tu navegador');
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        setError('Este email ya está registrado con otro método');
      } else if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
        setError('Error al registrarse con Google: ' + (error.message || 'Error desconocido'));
      }
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        
        <div className="modal-header">
          <h2>📝 Registro</h2>
          <p>Únete a Starboy Records</p>
        </div>

        <form onSubmit={handleRegister} className="modal-form">
          <div className="form-group">
            <label htmlFor="displayName">👤 Nombre</label>
            <input
              type="text"
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Tu nombre"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">📧 Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">🔒 Contraseña</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">🔒 Confirmar Contraseña</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={loading}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'Registrando...' : 'Registrarse'}
          </button>
        </form>

        <div className="divider">
          <span>O</span>
        </div>

        <button 
          type="button" 
          className="google-btn" 
          onClick={handleGoogleRegister}
          disabled={loading}
        >
          <img 
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" 
            alt="Google logo" 
            className="google-icon"
          />
          Continuar con Google
        </button>
      </div>
    </div>
  );
}

export default ModalRegister;
